class HomepageIconsImages extends HTMLElement {
  connectedCallback() {
    this.slider = this.querySelector('[data-slider]');
    this.cards = Array.from(this.querySelectorAll('[data-card]'));
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.activeIndex = 0;

    if (!this.slider || this.cards.length === 0) return;

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);

    this.slider.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goTo(index));
    });

    this.enableDrag();
    this.onResize();
    this.startAtInitialCard();

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        this.fitCaptions();
        if (this.isMobileLayout()) this.goTo(0, false);
      });
    }
  }

  disconnectedCallback() {
    this.slider?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    this.disableDrag();
  }

  isMobileLayout() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  isSliderLayout() {
    return this.isMobileLayout() || this.hasAttribute('data-desktop-slider');
  }

  defaultDesktopIndex() {
    return Math.floor((this.cards.length - 1) / 2);
  }

  startAtInitialCard() {
    if (this.isMobileLayout()) {
      this.goTo(0, false);
      return;
    }

    if (this.hasAttribute('data-desktop-slider') && this.cards.length > 1) {
      this.goTo(Math.min(1, this.cards.length - 1), false);
      return;
    }

    this.setActive(this.defaultDesktopIndex());
  }

  onResize() {
    this.fitCaptions();
    if (this.isMobileLayout()) {
      this.updateActiveFromScroll();
      return;
    }
    if (!this.isSliderLayout()) {
      this.setActive(this.defaultDesktopIndex());
      return;
    }
    this.updateActiveFromScroll();
  }

  fitCaptions() {
    const captions = Array.from(this.querySelectorAll('.homepage-icons-images__caption'));
    if (captions.length === 0) return;

    captions.forEach((caption) => {
      caption.style.fontSize = '';
    });

    const startSize = parseFloat(window.getComputedStyle(captions[0]).fontSize);
    if (!startSize) return;

    let size = startSize;
    const overflows = () => captions.some((caption) => caption.scrollWidth > caption.clientWidth);

    while (overflows() && size > 10) {
      size -= 0.5;
      captions.forEach((caption) => {
        caption.style.fontSize = `${size}px`;
      });
    }
  }

  onScroll() {
    if (this.scrollTick) return;
    this.scrollTick = true;
    requestAnimationFrame(() => {
      this.scrollTick = false;
      this.updateActiveFromScroll();
    });
  }

  updateActiveFromScroll() {
    if (!this.isSliderLayout()) return;

    const sliderRect = this.slider.getBoundingClientRect();
    const center = sliderRect.left + sliderRect.width / 2;
    let closest = 0;
    let min = Infinity;

    this.cards.forEach((card, index) => {
      const rect = card.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - center);
      if (distance < min) {
        min = distance;
        closest = index;
      }
    });

    this.setActive(closest);
  }

  setActive(index) {
    this.activeIndex = index;
    this.cards.forEach((card, i) => card.classList.toggle('is-active', i === index));
    this.dots.forEach((dot, i) => {
      const isActive = i === index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  goTo(index, smooth = true) {
    const card = this.cards[index];
    if (!card) return;

    const left = card.offsetLeft - (this.slider.clientWidth - card.offsetWidth) / 2;
    this.slider.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' });
    this.setActive(index);
  }

  enableDrag() {
    this.slider.addEventListener('pointerdown', this.onPointerDown);
  }

  disableDrag() {
    this.slider.removeEventListener('pointerdown', this.onPointerDown);
    this.slider.removeEventListener('pointermove', this.onPointerMove);
    this.slider.removeEventListener('pointerup', this.onPointerUp);
    this.slider.removeEventListener('pointercancel', this.onPointerUp);
  }

  onPointerDown(event) {
    if (event.pointerType === 'touch') return;
    if (!this.isSliderLayout()) return;

    this.dragging = true;
    this.dragStartX = event.clientX;
    this.dragScrollLeft = this.slider.scrollLeft;
    this.slider.classList.add('is-dragging');
    this.slider.setPointerCapture(event.pointerId);
    this.slider.addEventListener('pointermove', this.onPointerMove);
    this.slider.addEventListener('pointerup', this.onPointerUp);
    this.slider.addEventListener('pointercancel', this.onPointerUp);
  }

  onPointerMove(event) {
    if (!this.dragging) return;
    event.preventDefault();
    this.slider.scrollLeft = this.dragScrollLeft - (event.clientX - this.dragStartX);
  }

  onPointerUp() {
    this.dragging = false;
    this.slider.classList.remove('is-dragging');
    this.slider.removeEventListener('pointermove', this.onPointerMove);
    this.slider.removeEventListener('pointerup', this.onPointerUp);
    this.slider.removeEventListener('pointercancel', this.onPointerUp);
  }
}

customElements.define('homepage-icons-images', HomepageIconsImages);
